import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import type { ArtistDetail, ChartPage, ForgottenTrack, OnThisDay, SearchResult, Station, TrackDetail } from "./types";

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

type Params = Record<string, string | number | undefined | null>;

export async function get<T>(path: string, params: Params = {}, signal?: AbortSignal): Promise<T> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  const res = await fetch(`/v1${path}${qs.size ? `?${qs}` : ""}`, { signal });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body?.error?.code ?? "error", body?.error?.message ?? res.statusText);
  }
  return res.json() as Promise<T>;
}

const q = <T,>(key: unknown[], path: string, params?: Params, opts?: Partial<UseQueryOptions<T>>) =>
  useQuery<T>({ queryKey: key, queryFn: ({ signal }) => get<T>(path, params, signal), ...opts });

export const useStations = () =>
  q<{ stations: Station[] }>(["stations"], "/stations", undefined, { staleTime: 10 * 60_000 });

export const useChart = (chart: string, date: string) =>
  q<ChartPage>(["chart", chart, date], `/charts/${chart}/${date}`, { nearest: 1 }, { staleTime: 5 * 60_000 });

export const useChartDates = (chart: string, enabled: boolean) =>
  q<{ dates: string[] }>(["dates", chart], `/charts/${chart}/dates`, undefined, { enabled, staleTime: 30 * 60_000 });

export const useTrack = (id: string) => q<TrackDetail>(["track", id], `/tracks/${id}`);

export const useSearch = (term: string, params: Params = {}, enabled = true) =>
  q<SearchResult>(["search", term, params], "/search", { q: term, ...params }, { enabled: enabled && term.trim().length > 0 });

export const useOnThisDay = () => q<OnThisDay>(["on-this-day"], "/on-this-day", undefined, { staleTime: 60 * 60_000 });

export const useForgottenPreview = () =>
  q<{ tracks: ForgottenTrack[] }>(["forgotten-preview"], "/discover/forgotten", { limit: 6, min_entries: 15, unseen_years: 5 });

export const getForgotten = (params: Params) => get<{ filters: Params; tracks: ForgottenTrack[] }>("/discover/forgotten", params);
export const getRandom = (params: Params) => get<{ track: ForgottenTrack | null }>("/discover/random", params);
export const getArtist = (id: string | number, params: Params) => get<ArtistDetail>(`/artists/${id}`, params);
